import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { WBSButton } from '@/common/WBSButton';
import { CancelPendingRequestPanel } from '@/follow-up/CancelPendingRequestPanel';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import { GroupFollowUpRequestDetail } from '@/follow-up/GroupFollowUpRequestDetail';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/pending-requests/view/$requestId',
)({
  component: ViewPendingRequestScreen,
});

/**
 * Subnav 2's request detail —
 * `groups/single/study-group/follow-ups/pending-requests/view.php` plus
 * `view-admins-follow-up-requests.php`.
 *
 * ONE renderer, not two. Legacy branches at `view.php:30-34` on
 * `creator_id == student_id` into `view-members-follow-up-requests.php` (the
 * student's own self-request, with Accept/Reject). That arm is unreachable three
 * ways over: D36 dropped all 15 migrated self-requests, `creator_id` FKs
 * `study_helpers.id` so a student can never become a creator, and this group's
 * create path always has a distinct helper picking a distinct student. R-2.
 *
 * A `rejected` request reaches this page by the ordinary route, not by URL
 * typing: the list renders rows that are `pending` when it loads and the detail
 * loads the request fresh, so a student who rejects in between — which is the
 * outcome the helper is waiting on — puts a rejected request here. That is the
 * only way one reaches this template in legacy either, and it is what the
 * rejection-reason block and the Dismiss button exist for.
 */
function ViewPendingRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName, requestId } = Route.useParams();
  const { setCanceledRequestId } = useFollowUpsAlert();

  const [dismissed, setDismissed] = useState(false);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getGroupFollowUpRequest.useQuery(
    { path: { group_id: groupId ?? 0, request_id: Number(requestId) } },
    { enabled: groupId !== undefined },
  );

  const {
    mutateAsync: cancelRequest,
    isPending: cancelling,
    error: cancelError,
  } = api.cancelGroupFollowUpRequest.useMutation();
  const {
    mutateAsync: dismissRejection,
    isPending: dismissing,
    error: dismissError,
  } = api.dismissGroupFollowUpRequest.useMutation();

  const backToSubnav = () =>
    void navigate({
      to: '/groups/$groupName/follow-ups/pending-requests/',
      params: { groupName },
    });

  const handleCancel = async (reason: string): Promise<void> => {
    if (groupId === undefined) return;
    try {
      await cancelRequest({
        path: { group_id: groupId, request_id: Number(requestId) },
        body: { reason },
      });
    } catch {
      // Rendered from `cancelError`. The request keeps its status, so the panel
      // stays open with the typed reason still in it.
      return;
    }
    setCanceledRequestId(requestId);
    // Both lists move: the row leaves subnav 2 (`status = PENDING`) and joins
    // subnav 1, whose default exclusion hides `pending` and `in_progress` but
    // not `canceled` — 18 of the 93 default-visible migrated rows are canceled.
    await Promise.all([
      api.listGroupPendingFollowUpRequests.invalidateQueries(),
      api.listGroupFollowUpRequests.invalidateQueries(),
    ]);
    backToSubnav();
  };

  const handleDismiss = async (): Promise<void> => {
    if (groupId === undefined) return;
    try {
      await dismissRejection({
        path: { group_id: groupId, request_id: Number(requestId) },
      });
    } catch {
      // Rendered from `dismissError`.
      return;
    }
    // Legacy redirects to the pending list carrying a bp message
    // (`class-follow-up-request.php:287`), and this stays put instead —
    // deliberately. The request being dismissed is `rejected`, and the pending
    // list is `status = PENDING`, so legacy's redirect lands on a list that
    // never contained the row and shows a banner about a request that is not
    // there. Confirming in place is the same information next to the thing it
    // happened to. The banner channel the list would need is the layout's alert
    // context, which carries three fixed slots and no dismissed one.
    //
    // Nothing is invalidated, and that is not an oversight: `rejection_dismissed`
    // is on no read path anywhere in v3 (DIV-3 removed the predicate from every
    // query and the column is not on `GroupFollowUpRequestDetail`), so a refetch
    // would return a byte-identical payload. `can_dismiss` is
    // `authorized AND status == REJECTED` and dismissing changes neither, so the
    // button has to be retired by `dismissed` rather than by the server —
    // legacy solves the same problem by redirecting away.
    setDismissed(true);
  };

  // 404 for a request outside the group's derived roster (DIV-4), same screen as
  // an unknown id.
  if (isError) return <NotFoundScreen />;
  if (isLoading || detail === undefined) return null;

  const writeError = cancelError ?? dismissError;

  return (
    <GroupFollowUpRequestDetail detail={detail} onBack={backToSubnav}>
      {dismissed && (
        <AlertBox variant="success">
          {t('wbs.follow-up-request-rejection-dismissed')}
        </AlertBox>
      )}
      {writeError != null && (
        <AlertBox variant="error">
          {/* Translation Needed — the same EN-only fallback
              `FollowUpVerifyModal` uses for a coded refusal with no message of
              its own, e.g. the 409 on a request that stopped being pending. */}
          {errorMessage(
            writeError,
            t('wbs.there-was-a-problem-with-your-submission'),
          )}
        </AlertBox>
      )}

      {/* Both blocks are STATUS-GATED (`view-admins-follow-up-requests.php:13`
          and `:22`) and neither takes an empty-value fallback: `:17` and `:26`
          are bare `echo`s, unlike every block on subnav 1's detail. The empty
          box is the common case on both — `message_for_follow_up_coordinator`
          is empty on 1 of the 2 migrated pending rows and
          `message_for_study_helper_reject` on 20 of 27 rejected — and reaching
          for 'No message' here would make the two detail routes disagree with
          legacy in opposite directions. */}
      {detail.status === 'pending' && (
        <FollowUpNotes
          // Unit 22806, 'if Accepted'. NOT `wbs.your-note-to-the` (unit 22802),
          // whose trailing colon legacy does not render, and NOT `-4`, which is
          // subnav 1's 'when Accepted'.
          title={t('wbs.your-note-to-the-2')}
          message={detail.message_for_follow_up_coordinator}
        />
      )}
      {detail.status === 'rejected' && (
        <FollowUpNotes
          title={t('lessons.student-s-reason-for')}
          message={detail.message_for_study_helper_reject}
        />
      )}

      {/* `can_cancel` and `can_dismiss` are computed by the server and are each
          the AND of the authorization (the student's ASSIGNED study helper — not
          the creator, not a group admin) and the status branch legacy puts one
          level inside its action div (`:38` Cancel on pending, `:45` Dismiss on
          rejected, no else). Re-deriving either here is how a client renders
          Cancel over a row the write then 409s. */}
      {detail.can_cancel && (
        <CancelPendingRequestPanel
          onConfirm={(reason) => void handleCancel(reason)}
          submitting={cancelling}
        />
      )}
      {detail.can_dismiss && !dismissed && (
        <div>
          <WBSButton
            wide
            type="button"
            onClick={() => void handleDismiss()}
            isLoading={dismissing}
          >
            {t('wbs.dismiss')}
          </WBSButton>
        </div>
      )}
    </GroupFollowUpRequestDetail>
  );
}
